import { useEffect, useState } from 'react'
import { api, type ConnectorSettings, type ConnectorTest } from '../api'

/**
 * Configuring the guideline mailbox — TECHNICAL_DESIGN.md §8.2, §8.6, §10.1.
 *
 * **The screen exists for one setting.** Host, user and folder are ordinary and
 * would have been fine in a file. The trusted `authserv-id` is not: §8.6 refuses
 * any submission whose DMARC alignment cannot be established, alignment is
 * decided during an SMTP transaction this process never saw, and the only
 * evidence available is a header that anybody — including the sender — can
 * write. So the header is read only when it names the server named here, and no
 * default can be right, because the value is whatever the firm's own mail server
 * calls itself.
 *
 * Left unset, every message is refused. That is the correct direction and it
 * looks exactly like a fault: a queue of DMARC refusals for legitimate mail, and
 * the natural next move is to look for a way to turn the check off. There isn't
 * one. **So this screen shows what the mailbox actually contains instead** — the
 * verdict per message and the authserv-ids present on real mail, which is the
 * value an administrator recognises and nothing here can guess.
 *
 * **The candidates are offered, never applied.** Choosing the topmost
 * automatically would configure the appliance to trust whichever server the last
 * message happened to pass through, and a forged header would be nominating its
 * own author as the authority.
 *
 * **Testing is not enabling and saving is not enabling.** An administrator gets
 * to fill the form in, read what came back, and only then turn the connector on;
 * nothing here starts accepting client instructions as a side effect.
 *
 * **The password is write-only.** It is never sent to this screen, so the field
 * is left blank to keep the stored one — otherwise every unrelated edit would
 * clear a working credential.
 */
/**
 * Why a message is not aligned, out of the four things it can be.
 *
 * *Not aligned — would be refused* is true and is where the operator stops. The
 * next step is different in each case: name the server, correct the name, fix
 * DMARC at the sender, or accept that this host writes no verdict at all — and
 * the probe already carries everything needed to say which, because it reports
 * the authserv-ids the message actually bore.
 *
 * Nothing here relaxes the check. §8.6's default is refusal because *"an
 * attacker can put a perfectly formed `Authentication-Results: dmarc=pass` in
 * the message"*, and absent is not aligned. This only explains the refusal.
 */
export function whyUnaligned(observed: string[], configured: string): string {
  if (!configured.trim()) {
    return 'No trusted server is named above, so nothing can be checked. Pick one below.'
  }
  if (observed.length === 0) {
    return 'This message carries no Authentication-Results header at all — your mail host is not recording a DMARC verdict, so none can be trusted.'
  }
  const wanted = configured.trim().toLowerCase()
  if (!observed.some((id) => id.toLowerCase() === wanted)) {
    return `Its verdict was written by ${observed.join(', ')}, not by ${configured.trim()}. Either that is your server and the name above is wrong, or the header is somebody else's and is not evidence.`
  }
  return `${configured.trim()} checked this message and did not pass it — the sender's domain failed DMARC, which is the case this refusal is for.`
}

export function MailboxSettings({
  senderAuthenticationOff = false,
}: {
  /** Whether §8.6's gate is currently off. Shown here rather than on every
   *  screen: the state affects inbound mail and nothing else, the environment
   *  gate already guarantees a customer installation cannot be in it, and a
   *  banner on screens it does not concern is one people learn to read past.
   *  This is the screen somebody is on when they need to know. */
  senderAuthenticationOff?: boolean
} = {}) {
  const [settings, setSettings] = useState<ConnectorSettings | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [form, setForm] = useState({
    host: '',
    username: '',
    password: '',
    authserv_id: '',
    mailbox: 'INBOX',
    port: '',
    starttls: false,
    insecure: false,
    search: 'UNSEEN',
  })
  const [probe, setProbe] = useState<ConnectorTest | null>(null)
  const [polled, setPolled] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  async function load() {
    setError(null)
    try {
      const found = await api.connectorSettings()
      setSettings(found)
      if (found) {
        setForm({
          host: found.host,
          username: found.username,
          password: '',
          authserv_id: found.authserv_id ?? '',
          mailbox: found.mailbox,
          port: String(found.port),
          starttls: found.starttls,
          insecure: found.insecure,
          search: found.search,
        })
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoaded(true)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function save() {
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      const updated = await api.saveConnectorSettings({
        host: form.host,
        username: form.username,
        // Blank means keep. The screen never received it and cannot send it back.
        password: form.password || null,
        authserv_id: form.authserv_id || null,
        mailbox: form.mailbox,
        port: form.port ? Number(form.port) : null,
        starttls: form.starttls,
        insecure: form.insecure,
        search: form.search,
        // Saving leaves it on or off as it was. Turning it on is its own act (§8.6).
        enabled: settings?.enabled ?? false,
      })
      setSettings(updated)
      setForm((f) => ({ ...f, password: '' }))
      setSaved(true)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /** On or off at once, and nothing else: the stored settings go back as they are, so
   *  an edit still in the form is not saved along with it. A tick box that waited for
   *  Save was ticked, left, and read as on while nothing was read. */
  async function turn(on: boolean) {
    if (!settings) return
    setBusy(true)
    setError(null)
    setSaved(false)
    try {
      setSettings(
        await api.saveConnectorSettings({
          host: settings.host,
          username: settings.username,
          password: null,
          authserv_id: settings.authserv_id,
          mailbox: settings.mailbox,
          port: settings.port,
          starttls: settings.starttls,
          insecure: settings.insecure,
          search: settings.search,
          enabled: on,
        }),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function test() {
    setBusy(true)
    setError(null)
    setProbe(null)
    try {
      // The *saved* settings, not the form. Reporting on a configuration the
      // connector will never use is the failure this is here to prevent.
      setProbe(await api.testConnector())
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!loaded) return <p className="loading">Loading…</p>

  const candidates = [
    ...new Set((probe?.messages ?? []).flatMap((m) => m.authserv_ids)),
  ].filter((id) => id !== form.authserv_id)

  return (
    <section className="mailbox-settings">
      {senderAuthenticationOff && (
        <p className="unauthenticated-banner" role="alert" data-testid="dmarc-relaxed">
          <strong>Sender authentication is off on this appliance.</strong> Mail is
          being accepted without establishing who sent it, so anyone who can email
          this mailbox can submit as any authorised sender. Turn it back on from{' '}
          <strong>Appliance</strong>.
        </p>
      )}
      <h2>Guidelines Mailbox Setup</h2>

      <p className="note">
        Optional. Without it, limits are still entered here or imported from a
        workbook as usual. Nothing that arrives by mail becomes a live limit on
        its own — it waits for an approver, like every other change.
      </p>

      {settings?.source === 'environment' && (
        <p className="warning">
          These settings currently come from a configuration file on the server.
          If you save here, this screen takes over and changes to that file will
          no longer have any effect.
        </p>
      )}

      {!settings && (
        <p className="note">
          The mailbox is not configured anywhere yet. Nothing is being read.
        </p>
      )}

      {/* Saving is not turning on (§8.6), so a mailbox can be saved and read nothing.
          Said at the top, where somebody who saved and sees no mail arriving looks. */}
      {settings && (
        <p className={settings.enabled ? 'ok' : 'warning'} data-testid="mailbox-state">
          {settings.enabled
            ? 'On: the worker reads the mailbox every 5 minutes. Scheduled Jobs shows what each read came to. '
            : 'Set up and not turned on, so nothing is read yet. Turn it on when the test below looks right. '}
          <button className="ghost" disabled={busy} onClick={() => void turn(!settings.enabled)}>
            {settings.enabled ? 'Turn off' : 'Turn on'}
          </button>
        </p>
      )}

      {error && <p className="error">{error}</p>}
      {saved && <p className="ok">Saved.</p>}
      {polled && <p className="ok">{polled}</p>}

      <div className="form">
        <label>
          IMAP host
          <input
            value={form.host}
            onChange={(e) => setForm({ ...form, host: e.target.value })}
            placeholder="mail.privateemail.com"
          />
        </label>
        <label>
          Mailbox user
          <input
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            placeholder="guidelines@yourfirm.com"
          />
        </label>
        <label>
          Password
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={settings?.password_set ? 'unchanged' : ''}
          />
          <small>
            {settings?.password_set
              ? 'A password is saved. Leave this blank to keep it — it is never shown again.'
              : 'Required, so this system can sign in to the mailbox. It is never shown again once saved.'}
          </small>
        </label>
        <label>
          Folder
          <input
            value={form.mailbox}
            onChange={(e) => setForm({ ...form, mailbox: e.target.value })}
          />
        </label>
        <label>
          Which messages
          <input
            value={form.search}
            onChange={(e) => setForm({ ...form, search: e.target.value })}
          />
          <small>
            <code>UNSEEN</code> reads only new mail, which is what you normally
            want. <code>ALL</code> re-reads the whole folder; nothing is
            duplicated if you do, because a message already handled is
            recognised.
          </small>
        </label>
        <label>
          Port
          <input
            value={form.port}
            onChange={(e) => setForm({ ...form, port: e.target.value })}
            placeholder={form.starttls || form.insecure ? '143' : '993'}
          />
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.starttls}
            onChange={(e) => setForm({ ...form, starttls: e.target.checked })}
          />
          STARTTLS on 143 rather than implicit TLS on 993
        </label>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={form.insecure}
            onChange={(e) => setForm({ ...form, insecure: e.target.checked })}
          />
          Plaintext IMAP — no encryption
        </label>
        {form.insecure && (
          <p className="warning">
            Client mandate data would cross the network unencrypted. This exists so
            a demonstration against a local server is possible without pretending
            it is safe; never point it at a real mailbox.
          </p>
        )}
      </div>

      <h3>The trusted mail server</h3>
      <p className="note">
        A message is refused unless we can be sure who really sent it. Your own
        mail server works that out as the message arrives, and records its finding
        in the{' '}
        <code>Authentication-Results</code> header your server writes, and so can
        anybody, including whoever sent the message. So the header is read{' '}
        <strong>only</strong> when it names the server below, and only the topmost
        such header counts.
      </p>
      <label>
        authserv-id
        <input
          value={form.authserv_id}
          onChange={(e) => setForm({ ...form, authserv_id: e.target.value })}
          placeholder="mx.yourfirm.com"
        />
        <small>
          Leave it empty and every message is refused, because no sender can be
          confirmed without it. Test the connection below to see the name your mail
          server writes.
        </small>
      </label>

      <div className="actions">
        <button onClick={() => void save()} disabled={busy}>
          Save
        </button>
        <button onClick={() => void test()} disabled={busy || !settings}>
          Test connection
        </button>
        {/* The same single pass the timer runs, for the administrator who has
            just configured one and wants to see it work. Not a replacement for
            the timer: a connector that only ran when somebody remembered is a
            client instruction waiting on attention nobody scheduled. */}
        <button
          className="ghost"
          disabled={busy || !settings?.enabled}
          title={
            settings?.enabled
              ? undefined
              : 'Turn it on first — this reads messages for real'
          }
          onClick={() => {
            setBusy(true)
            setError(null)
            setPolled(null)
            api
              .pollConnector()
              .then((r) =>
                setPolled(
                  r.detail ??
                    `${r.read} part(s) read — ` +
                      (Object.entries(r.outcomes)
                        .map(([k, n]) => `${n} ${k.replace(/_/g, ' ')}`)
                        .join(', ') || 'nothing new'),
                ),
              )
              .catch((e) => setError(e instanceof Error ? e.message : String(e)))
              .finally(() => setBusy(false))
          }}
        >
          Read the mailbox now
        </button>
      </div>
      <p className="note">
        Saving does not turn it on, and testing does not either, so the settings can be
        got right before anything is read. <strong>Turn on</strong>, at the top, takes
        effect at once.
      </p>

      {probe && !probe.reached && (
        <div className="probe">
          <h3>Could not read the mailbox</h3>
          <p className="error">{probe.detail}</p>
        </div>
      )}

      {probe?.reached && (
        <div className="probe">
          <h3>
            {probe.messages.length} message(s) in {probe.mailbox}
          </h3>
          <p className="note">
            Nothing was written and nothing was marked read.
          </p>

          {candidates.length > 0 && (
            <div className="candidates">
              <p>
                Your mail carries these authserv-ids. Pick the one you recognise as
                your own receiving server — we cannot tell which is yours, and a
                header naming somebody else may have been written by the sender.
              </p>
              {candidates.map((id) => (
                <button key={id} onClick={() => setForm({ ...form, authserv_id: id })}>
                  {id}
                </button>
              ))}
              <small>Chosen here, then saved — nothing is applied for you.</small>
            </div>
          )}

          <table>
            <thead>
              <tr>
                <th>From</th>
                <th>Subject</th>
                <th>DMARC</th>
                <th>Readable attachments</th>
              </tr>
            </thead>
            <tbody>
              {probe.messages.map((m, i) => (
                <tr key={i}>
                  <td>{m.sender_address}</td>
                  <td>{m.subject ?? <em>(no subject)</em>}</td>
                  <td className={m.dmarc_aligned ? 'ok' : 'refused'}>
                    {m.dmarc_aligned ? 'Aligned' : 'Not aligned — would be refused'}
                    {!m.dmarc_aligned && (
                      <span className="clause" data-testid="why-unaligned">
                        {whyUnaligned(m.authserv_ids, form.authserv_id)}
                      </span>
                    )}
                  </td>
                  <td>
                    {m.attachments.length > 0 ? (
                      m.attachments.join(', ')
                    ) : (
                      <em>nothing this can read</em>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {probe.messages.length === 0 && (
            <p className="note">
              The folder is empty, or every message in it is already marked read —
              send one to the mailbox and test again.
            </p>
          )}
        </div>
      )}
    </section>
  )
}
